import { useState, useEffect, useRef } from 'react'
import { Box, Flex, Text, Spinner, IconButton, HStack, Tooltip } from '@chakra-ui/react'
import { Play, DownloadSimple, FilmStrip } from '@phosphor-icons/react'
import VideoPlayer from './index'

interface VideoThumbnailProps {
  src: string
  maxWidth?: string
  maxHeight?: string
  isOwn?: boolean
}

// Formatar duração em mm:ss
const formatDuration = (seconds: number) => {
  if (isNaN(seconds) || !isFinite(seconds)) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function VideoThumbnail({ src, maxWidth = '280px', maxHeight = '200px', isOwn = false }: VideoThumbnailProps) {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null)
  const [duration, setDuration] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [error, setError] = useState(false)
  const loadingRef = useRef(true)

  // Verificar se é URL cross-origin
  const isCrossOrigin = (url: string) => {
    try {
      const videoUrl = new URL(url, window.location.origin)
      return videoUrl.origin !== window.location.origin
    } catch {
      return true
    }
  }

  // Carregar apenas duração do vídeo (sem thumbnail para cross-origin)
  useEffect(() => {
    loadingRef.current = true
    setIsLoading(true)
    setError(false)
    setThumbnailUrl(null)
    
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'metadata'
    
    const crossOrigin = isCrossOrigin(src)
    
    // Rede/CDN lenta: não abortar preview em 3s; fallback só após timeout maior
    const timeoutId = setTimeout(() => {
      if (loadingRef.current) {
        loadingRef.current = false
        setError(true)
        setIsLoading(false)
        video.src = ''
      }
    }, 12000)
    
    video.onloadedmetadata = () => {
      const d = video.duration
      if (isFinite(d) && !isNaN(d)) setDuration(d)
      
      // Se for cross-origin, não tenta gerar thumbnail (causa erro de segurança)
      if (crossOrigin) {
        loadingRef.current = false
        setError(true) // Usa o fallback visual
        setIsLoading(false)
        clearTimeout(timeoutId)
        return
      }
      
      // duration inválida impede seek — evita spinner infinito sem onseeked
      const seekTarget = Math.min(1, (isFinite(d) && d > 0 ? d * 0.1 : 0.5))
      if (!isFinite(seekTarget) || isNaN(seekTarget)) {
        loadingRef.current = false
        setError(true)
        setIsLoading(false)
        clearTimeout(timeoutId)
        return
      }
      video.currentTime = seekTarget
    }
    
    video.onseeked = () => {
      if (!loadingRef.current || crossOrigin) return
      try {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth || 320
        canvas.height = video.videoHeight || 180
        const ctx = canvas.getContext('2d')
        
        if (ctx) {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
          if (dataUrl && dataUrl.length > 100) {
            setThumbnailUrl(dataUrl)
          } else {
            setError(true)
          }
        } else {
          setError(true)
        }
        loadingRef.current = false
        setIsLoading(false)
        clearTimeout(timeoutId)
      } catch (e) {
        // SecurityError para canvas tainted - usa fallback
        loadingRef.current = false
        setError(true)
        setIsLoading(false)
        clearTimeout(timeoutId)
      }
    }
    
    video.onerror = () => {
      loadingRef.current = false
      setError(true)
      setIsLoading(false)
      clearTimeout(timeoutId)
    }
    
    video.src = src
    video.load()
    
    return () => {
      loadingRef.current = false
      clearTimeout(timeoutId)
      video.src = ''
    }
  }, [src])

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation()
    const link = document.createElement('a')
    link.href = src
    link.download = src.split('/').pop() || 'video.mp4'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Cores baseadas em quem enviou
  const borderColor = isOwn ? 'yellow.500' : 'gray.600'

  return (
    <>
      <Box
        position="relative"
        maxW={maxWidth}
        borderRadius="lg"
        overflow="hidden"
        border="2px solid"
        borderColor={borderColor}
        bg="gray.800"
      >
        {/* Thumbnail ou fallback */}
        <Box
          cursor="pointer"
          onClick={() => setIsOpen(true)}
          _hover={{
            '& .play-overlay': {
              opacity: 1,
            },
          }}
        >
          {isLoading ? (
            <Flex
              w="100%"
              minW="200px"
              h="140px"
              bg="gray.800"
              align="center"
              justify="center"
              direction="column"
              gap={2}
            >
              <Spinner size="md" color="yellow.400" />
              <Text color="gray.500" fontSize="xs">
                Carregando preview...
              </Text>
            </Flex>
          ) : error || !thumbnailUrl ? (
            <Flex
              w="100%"
              minW="200px"
              h="140px"
              bg="gray.800"
              align="center"
              justify="center"
              direction="column"
              gap={2}
            >
              <Box
                bg="gray.700"
                borderRadius="full"
                p={3}
              >
                <FilmStrip size={32} color="#ECC94B" weight="duotone" />
              </Box>
              <Text color="gray.400" fontSize="sm">
                Vídeo
              </Text>
              {duration > 0 && (
                <Text color="gray.500" fontSize="xs">
                  {formatDuration(duration)}
                </Text>
              )}
            </Flex>
          ) : (
            <Box position="relative">
              <Box
                as="img"
                src={thumbnailUrl}
                alt="Video thumbnail"
                w="100%"
                maxW={maxWidth}
                maxH={maxHeight}
                objectFit="cover"
              />
              {/* Play overlay */}
              <Flex
                className="play-overlay"
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                bg="blackAlpha.500"
                opacity={0.8}
                transition="opacity 0.2s"
              >
                <Box
                  bg="whiteAlpha.900"
                  borderRadius="full"
                  p={3}
                  boxShadow="lg"
                >
                  <Play size={24} weight="fill" color="black" />
                </Box>
              </Flex>
              {/* Duration badge */}
              {duration > 0 && (
                <Box
                  position="absolute"
                  bottom={2}
                  left={2}
                  bg="blackAlpha.800"
                  px={2}
                  py={0.5}
                  borderRadius="md"
                >
                  <Text fontSize="xs" color="white" fontWeight="semibold">
                    {formatDuration(duration)}
                  </Text>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Download button - sempre visível */}
        <HStack
          position="absolute"
          top={2}
          right={2}
          spacing={1}
        >
          <Tooltip label="Baixar vídeo">
            <IconButton
              aria-label="Baixar vídeo"
              icon={<DownloadSimple size={16} weight="bold" />}
              size="xs"
              bg="blackAlpha.700"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={handleDownload}
            />
          </Tooltip>
        </HStack>
      </Box>

      {/* Video Player Modal */}
      <VideoPlayer
        src={src}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onDownload={() => handleDownload({} as React.MouseEvent)}
      />
    </>
  )
}

export default VideoThumbnail
